import {createContext, useContext} from 'react';

import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {CardAttributeUpdateAction} from '../../helpers/models/cardAttributeUpdateHelpers';
import {
  ConflictingMergeCardValues,
  ContactDetailsConflictingFieldKeysEnum,
  ContactDetailsSectionWithConflictsEnum,
} from '../../helpers/models/cardMergeHelpers';
import {globalNamespace} from '../../helpers/models/namespaceHelpers';
import {CardModel} from '../../models/api/cardModel';
import {ContactModel} from '../../models/api/contactModel';
import {OverrideMergeValues} from '../../store/data/cards/cardsActions';
import {
  ContactDetailsPanelId,
  makeAppPanelId,
  makeContactDetailsPanelId,
  makeInboxesPanelId,
  makeViewerPanelId,
} from '../../store/panels/panelStateBase';
import {
  ContactDetailsTrackEventHandler,
  ContactDetailsTrackingHandler,
} from './contactDetailsTrackingHelpers';

/*
 * Constants.
 */

/**
 * Controls the version of contact details that should be displayed.
 * Version 2 is contact details with accounts.
 * TODO: Once accounts is released to GA, remove this as everything here
 * can be on version 2
 */
export enum ContactDetailsVersionsEnum {
  VERSION_1 = 'v1',
  VERSION_2 = 'v2',
}

export const defaultContactDetailsContact: ContactDetailsContextProps = {
  version: ContactDetailsVersionsEnum.VERSION_1,
  // TODO: Do something better than generate a random panel ID.
  panelId: makeContactDetailsPanelId(makeViewerPanelId(makeInboxesPanelId(makeAppPanelId()))),
  isReadOnly: false,
  isPopup: false,
  shouldHideHeaderTabs: false,
  conversationNamespace: globalNamespace,
  onTrackEvent: ContactDetailsTrackingHandler,
  updateSelectedCard: () => {},
  createNewCardAsync: () => Promise.resolve(undefined),
  mergeContext: {
    isMerging: false,
    conflictingMergeValues: undefined,
    overrideMergeValues: undefined,
    startMerge: () => {},
    cancelMerge: () => {},
    confirmMerge: () => {},
    setMergeOverrides: () => {},
  },
  storedActionsContext: {
    storedActions: [],
    unsavedCard: undefined,
    onSetUnsavedCard: () => {},
    clearStoredActions: () => {},
    onUpdateUnsavedCard: () => {},
  },
};

/*
 * Types.
 */

export type CreateCardAsyncHandler = (
  name: string,
  contact: ContactModel,
  namespace?: string,
) => Promise<CardModel | undefined>;

/*
 * Props.
 */

export interface SetMergeOverridesNameOption {
  section: ContactDetailsSectionWithConflictsEnum.HEADER;
  sectionKey: ContactDetailsConflictingFieldKeysEnum.NAME;
  name: string;
}

export interface SetMergeOverridesAccountOption {
  section: ContactDetailsSectionWithConflictsEnum.HEADER;
  sectionKey: ContactDetailsConflictingFieldKeysEnum.ACCOUNT;
  accountId: number;
}

export interface SetMergeOverridesCustomFieldOption {
  section: ContactDetailsSectionWithConflictsEnum.CONTACT_INFORMATION;
  customFieldId: number;
  customFieldAttribute: string;
}

export type SetMergeOverridesOptions =
  | SetMergeOverridesNameOption
  | SetMergeOverridesAccountOption
  | SetMergeOverridesCustomFieldOption;

export type SetMergeOverridesHandler = (options: SetMergeOverridesOptions) => void;

export interface ContactDetailsMergeContextProps {
  isMerging: boolean;
  conflictingMergeValues: ConflictingMergeCardValues | undefined;
  overrideMergeValues: OverrideMergeValues | undefined;
  startMerge: (mainCardId: number, mergeCardIds: ReadonlyArray<number>) => void;
  cancelMerge: Handler;
  confirmMerge: Handler;
  setMergeOverrides: SetMergeOverridesHandler;
}

export interface ContactDetailsStoredActionsContextProps {
  storedActions: ReadonlyArray<CardAttributeUpdateAction>;
  unsavedCard: CardModel | undefined;
  clearStoredActions: Handler;
  onSetUnsavedCard: HandlerOf<CardModel | undefined>;
  onUpdateUnsavedCard: HandlerOf<CardAttributeUpdateAction>;
}

export interface ContactDetailsContextProps {
  version: ContactDetailsVersionsEnum;
  panelId: ContactDetailsPanelId;
  isReadOnly: boolean;
  isPopup: boolean;
  shouldHideHeaderTabs: boolean;
  conversationNamespace: string | undefined;
  mergeContext: ContactDetailsMergeContextProps;
  storedActionsContext: ContactDetailsStoredActionsContextProps;
  updateSelectedCard: HandlerOf<number>;
  createNewCardAsync: CreateCardAsyncHandler;
  onTrackEvent?: ContactDetailsTrackEventHandler;
}

/*
 * Context.
 */

export const ContactDetailsContext = createContext<ContactDetailsContextProps>(defaultContactDetailsContact);

/*
 * Hook.
 */

export function useContactDetailsContext() {
  return useContext(ContactDetailsContext);
}
